import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, InputNumber, Modal, Space, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../../api/client';
import { fetchTransactionImageUrl, useAddInvestment, useInvestments } from '../../api/hooks';
import { useAuth } from '../../context/AuthContext';
import type { Investment } from '../../types';
import { apiDate, date, money, percent } from '../../utils/format';
import PendingDocumentPicker from './PendingDocumentPicker';
import TransactionImageLink from './TransactionImageLink';

export default function InvestmentsTab({ investorId }: { investorId: number }) {
  const { data, isLoading } = useInvestments(investorId);
  const { canWrite, hasRole } = useAuth();
  // Investors get a single statement PDF from the Ledger tab instead of per-row receipts.
  const showReceipts = !hasRole('Investor');
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [transactionImage, setTransactionImage] = useState<File | null>(null);
  const addInvestment = useAddInvestment(investorId);

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await addInvestment.mutateAsync({
        amount: values.amount as number,
        profitRate: values.profitRate as number,
        investmentDate: apiDate(values.investmentDate as dayjs.Dayjs)!,
        notes: values.notes as string | undefined,
        transactionImage,
      });
      message.success('Investment added.');
      form.resetFields();
      setTransactionImage(null);
      setOpen(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <>
      {canWrite && (
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            Add investment
          </Button>
        </Space>
      )}

      <Table<Investment>
        rowKey="id"
        loading={isLoading}
        dataSource={data ?? []}
        scroll={{ x: 'max-content' }}
        pagination={false}
        summary={(rows) => {
          const principal = rows.reduce((sum, r) => sum + r.remainingAmount, 0);
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}>
                <strong>Current principal</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                <strong>{money(principal)}</strong>
              </Table.Summary.Cell>
              {/* Rate, Status, Notes, Transaction proof — plus Receipt when it is shown. */}
              <Table.Summary.Cell index={5} colSpan={showReceipts ? 5 : 4} />
            </Table.Summary.Row>
          );
        }}
        columns={[
          { title: '#', dataIndex: 'id', width: 70 },
          { title: 'Date', dataIndex: 'investmentDate', render: date },
          { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
          { title: 'Withdrawn', dataIndex: 'withdrawnAmount', align: 'right', render: money },
          { title: 'Remaining', dataIndex: 'remainingAmount', align: 'right', render: money },
          { title: 'Rate', dataIndex: 'profitRate', align: 'right', render: percent },
          {
            title: 'Status',
            dataIndex: 'isActive',
            render: (active: boolean) => <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Closed'}</Tag>,
          },
          { title: 'Notes', dataIndex: 'notes', render: (v: string | null) => v ?? '—' },
          {
            title: 'Transaction proof',
            render: (_, row) => (
              <TransactionImageLink
                exists={row.hasTransactionImage}
                title={`Transaction proof — tranche #${row.id}`}
                fetchUrl={() => fetchTransactionImageUrl(investorId, row.id)}
              />
            ),
          },
          ...(showReceipts
            ? [
                {
                  title: 'Receipt',
                  render: (_: unknown, row: Investment) => (
                    <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() =>
                        downloadFile(`/receipts/investment/${row.id}`, `investment_receipt_${row.id}.pdf`).catch((err) =>
                          message.error(errorMessage(err)),
                        )
                      }
                    >
                      PDF
                    </Button>
                  ),
                },
              ]
            : []),
        ]}
      />

      <Modal
        title="Add investment tranche"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={addInvestment.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false} initialValues={{ investmentDate: dayjs() }}>
          <Form.Item
            name="amount"
            label="Amount (PKR)"
            rules={[{ required: true }, { type: 'number', min: 0.01, message: 'Amount must be greater than zero.' }]}
          >
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="profitRate"
            label="Monthly profit rate (%)"
            rules={[{ required: true }, { type: 'number', min: 0.01, max: 99.99, message: 'Rate must be 0.01–99.99.' }]}
          >
            <InputNumber min={0.01} max={99.99} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="investmentDate" label="Investment date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>

          <PendingDocumentPicker
            type="transaction-image"
            label="Transaction proof (optional)"
            hint="Bank slip, JazzCash/EasyPaisa screenshot or cash voucher · JPG, PNG or WebP · max 5 MB"
            file={transactionImage}
            onChange={setTransactionImage}
          />
        </Form>
      </Modal>
    </>
  );
}
